import React, { useState, useEffect } from "react";
import styles from "../styles/AlertPopup.module.css";

export interface AlertConfig {
  type: "error" | "success" | "warning" | "info";
  title: string;
  message: string;
  duration?: number; // Auto-close after X milliseconds (0 = no auto-close)
  onConfirm?: () => void;
  confirmText?: string;
  showCancel?: boolean;
  onCancel?: () => void;
  cancelText?: string;
}

interface AlertPopupProps {
  alert: AlertConfig | null;
  onClose: () => void;
}

const AlertPopup: React.FC<AlertPopupProps> = ({ alert, onClose }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    if (alert) {
      setIsVisible(true);
      setIsClosing(false);

      // Auto-close if duration is specified
      if (alert.duration && alert.duration > 0) {
        const timer = setTimeout(() => {
          handleClose();
        }, alert.duration);

        return () => clearTimeout(timer);
      }
    } else {
      setIsVisible(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alert]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsVisible(false);
      onClose();
    }, 200);
  };

  const handleConfirm = () => {
    if (alert?.onConfirm) {
      alert.onConfirm();
    }
    handleClose();
  };

  const handleCancel = () => {
    if (alert?.onCancel) {
      alert.onCancel();
    }
    handleClose();
  };

  if (!alert || !isVisible) return null;

  const getIconAndColors = () => {
    switch (alert.type) {
      case "error":
        return { icon: "[!]", prefix: "ERROR:" };
      case "success":
        return { icon: "[✓]", prefix: "SUCCESS:" };
      case "warning":
        return { icon: "[!]", prefix: "WARNING:" };
      case "info":
        return { icon: "[i]", prefix: "INFO:" };
      default:
        return { icon: "[i]", prefix: "INFO:" };
    }
  };

  const { icon, prefix } = getIconAndColors();

  return (
    <div className={`${styles.alertOverlay} ${isClosing ? styles.closing : ""}`}>
      <div
        className={`${styles.alertPopup} ${styles[alert.type]} ${isClosing ? styles.closing : ""}`}
      >
        <div className={styles.alertHeader}>
          <div className={styles.alertIcon}>{icon}</div>
          <div className={styles.alertTitle}>
            {prefix} {alert.title.toUpperCase()}
          </div>
        </div>

        <div className={styles.alertContent}>
          <div className={styles.alertMessage}>{alert.message}</div>
        </div>

        <div className={styles.alertActions}>
          {alert.showCancel && (
            <button
              className={`${styles.alertBtn} ${styles.alertBtnCancel}`}
              onClick={handleCancel}
            >
              {(alert.cancelText || "CANCEL").toUpperCase()}
            </button>
          )}
          <button
            className={`${styles.alertBtn} ${styles.alertBtnConfirm}`}
            onClick={handleConfirm}
          >
            {(alert.confirmText || "OK").toUpperCase()}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AlertPopup;
